import React from 'react';
import CalendarLink from './CalendarLink';
import { type Address } from '../shared/types';
import Map from './Map';

interface WeeklyPreviewProps {
  addresses: Address[];
  setAddresses?: React.Dispatch<React.SetStateAction<Address[]>>;
}

const WeeklyPreview: React.FC<WeeklyPreviewProps> = ({ addresses, setAddresses = () => {} }) => {
  const today = new Date();
  const startOfWeek = new Date(today);
  startOfWeek.setDate(today.getDate() - today.getDay());

  const weekDates = Array.from({ length: 7 }, (_, i) => {
    const date = new Date(startOfWeek);
    date.setDate(startOfWeek.getDate() + i);
    return date;
  });

  const dayMap: { [key: number]: string } = {
    0: 'SU', 1: 'MO', 2: 'TU', 3: 'WE', 4: 'TH', 5: 'FR', 6: 'SA'
  };

  const getASPTimes = (date: Date) => {
    const day = dayMap[date.getDay()];
    return addresses.flatMap(addr => 
      addr.regulations
        .filter(reg => reg.schedule.days.includes(day))
        .map(reg => ({
          time: `${reg.schedule.startTime}-${reg.schedule.endTime}`,
          side: reg.side,
          address: addr.address
        }))
    );
  };

  const dayAbbreviations = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];

  return (
    <>
      <div className="card-brutalist mb-8">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6 pb-4 border-b-2 border-mta-white">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-mta-orange rounded-full flex items-center justify-center">
              <span className="text-mta-white font-bold text-sm">2</span>
            </div>
            <h2 className="text-lg font-bold text-mta-white uppercase tracking-wide font-helvetica">
              Weekly Schedule
            </h2>
          </div>
          <CalendarLink 
            addresses={addresses}
            className="btn-brutalist btn-mta-yellow text-sm flex items-center justify-center gap-2 h-12"
          />
        </div>

        <div className="space-y-3">
          {weekDates.map((date) => {
            const aspTimes = getASPTimes(date);
            const isBlocked = aspTimes.length > 0;
            const isToday = date.toDateString() === today.toDateString();

            return (
              <div 
                key={date.toISOString()} 
                className={`flex border-3 transition-all bg-mta-black ${
                  isToday ? 'border-mta-yellow' : 'border-mta-white'
                }`}
              >
                <div className={`w-20 sm:w-24 flex flex-col items-center justify-center p-3 border-r-3 ${
                  isToday ? 'border-mta-yellow bg-mta-yellow text-mta-black' : 'border-mta-white'
                }`}>
                  <span className="font-bold text-sm uppercase tracking-wide">
                    {dayAbbreviations[date.getDay()]}
                  </span>
                  <span className="text-2xl font-bold">{date.getDate()}</span>
                </div>
                
                <div className={`flex-1 p-4 ${isBlocked ? 'bg-mta-red/20' : 'bg-mta-green/20'}`}>
                  {aspTimes.length > 0 ? (
                    aspTimes.map((asp, i) => (
                      <div key={i} className="mb-3 last:mb-0">
                        <div className="text-mta-silver text-xs uppercase tracking-wide truncate">
                          {asp.address}
                        </div>
                        <div className="font-bold text-xl text-mta-white font-helvetica">
                          {asp.time}
                        </div>
                        <div className="text-sm text-mta-gray uppercase">
                          {asp.side === 'N' ? 'North' : asp.side === 'S' ? 'South' : asp.side === 'E' ? 'East' : 'West'} Side
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="flex items-center h-full min-h-[52px]">
                      <span className="text-mta-green font-bold uppercase tracking-wide">
                        No Street Cleaning
                      </span>
                    </div>
                  )}
                </div>
                
                <div className={`w-3 ${isBlocked ? 'bg-mta-red' : 'bg-mta-green'}`}></div>
              </div>
            );
          })}
        </div>
      </div>

      <Map 
        addresses={addresses}
        onSignClick={(newAddr: Address) => setAddresses((prev: Address[]) => [...prev, newAddr])}
      />
    </>
  );
};

export default WeeklyPreview;
